import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { Plus, Trash2, RotateCcw, Play, Pause, X, Zap } from "lucide-react";

interface Puller {
	id: number;
	force: number;
	side: "left" | "right";
	color: string;
}

const LEFT_COLORS = [
	"#ef4444",
	"#f97316",
	"#f59e0b",
	"#dc2626",
	"#ea580c",
];
const RIGHT_COLORS = [
	"#3b82f6",
	"#6366f1",
	"#8b5cf6",
	"#2563eb",
	"#4f46e5",
];

const CART_WIDTH = 72;
const ROPE_EXTRA = 50;

export function TugOfWar() {
	const [pullers, setPullers] = useState<Puller[]>([]);
	const [nextId, setNextId] = useState(1);
	const [newForceLeft, setNewForceLeft] = useState(50);
	const [newForceRight, setNewForceRight] = useState(50);
	const [isAnimating, setIsAnimating] = useState(false);
	const [cartOffset, setCartOffset] = useState(0);
	const [cartVelocity, setCartVelocity] = useState(0);
	const animationRef = useRef<number | null>(null);
	const lastTimeRef = useRef<number>(0);

	const leftPullers = pullers.filter((p) => p.side === "left");
	const rightPullers = pullers.filter((p) => p.side === "right");

	const leftForce = leftPullers.reduce((sum, p) => sum + p.force, 0);
	const rightForce = rightPullers.reduce((sum, p) => sum + p.force, 0);
	const netForce = rightForce - leftForce;

	const addPuller = (side: "left" | "right") => {
		const force = side === "left" ? newForceLeft : newForceRight;
		const colors = side === "left" ? LEFT_COLORS : RIGHT_COLORS;
		const existing = pullers.filter((p) => p.side === side).length;
		const color = colors[existing % colors.length]!;
		setPullers((prev) => [...prev, { id: nextId, force, side, color }]);
		setNextId((prev) => prev + 1);
	};

	const removePuller = (id: number) => {
		setPullers((prev) => prev.filter((p) => p.id !== id));
	};

	const reset = useCallback(() => {
		setIsAnimating(false);
		setCartOffset(0);
		setCartVelocity(0);
		if (animationRef.current) {
			cancelAnimationFrame(animationRef.current);
			animationRef.current = null;
		}
	}, []);

	const clearAll = () => {
		setPullers([]);
		reset();
	};

	const toggleAnimation = () => {
		if (isAnimating) {
			setIsAnimating(false);
		} else {
			if (netForce === 0 && pullers.length === 0) return;
			setIsAnimating(true);
			lastTimeRef.current = 0;
		}
	};

	useEffect(() => {
		if (!isAnimating) {
			if (animationRef.current) {
				cancelAnimationFrame(animationRef.current);
				animationRef.current = null;
			}
			return;
		}

		const mass = 10;
		const friction = 0.3;
		const maxOffset = 400;

		const animate = (timestamp: number) => {
			if (lastTimeRef.current === 0) {
				lastTimeRef.current = timestamp;
			}
			const dt = Math.min((timestamp - lastTimeRef.current) / 1000, 0.05);
			lastTimeRef.current = timestamp;

			setCartVelocity((prev) => {
				const acceleration = netForce / mass;
				const frictionForce =
					prev !== 0 ? -Math.sign(prev) * friction : 0;
				let newVel = prev + (acceleration + frictionForce) * dt;

				if (netForce === 0 && Math.abs(newVel) < 0.5) {
					newVel = 0;
				}

				setCartOffset((prevOffset) => {
					const newOffset = prevOffset + newVel * dt * 3;
					if (Math.abs(newOffset) >= maxOffset) {
						setIsAnimating(false);
						return Math.sign(newOffset) * maxOffset;
					}
					return newOffset;
				});

				return newVel;
			});

			animationRef.current = requestAnimationFrame(animate);
		};

		animationRef.current = requestAnimationFrame(animate);

		return () => {
			if (animationRef.current) {
				cancelAnimationFrame(animationRef.current);
			}
		};
	}, [isAnimating, netForce]);

	useEffect(() => {
		reset();
	}, [pullers, reset]);

	const getMotionLabel = () => {
		if (netForce === 0)
			return { text: "Balanced — No Motion", color: "text-slate-500" };
		if (netForce > 0)
			return { text: "Cart Moves Right →", color: "text-blue-600" };
		return { text: "← Cart Moves Left", color: "text-red-600" };
	};

	const motion = getMotionLabel();

	return (
		<div className="h-full flex flex-col">
			{/* Top bar */}
			<div className="flex-shrink-0 flex items-center justify-between px-6 py-3 border-b border-slate-200/60 bg-white/70 backdrop-blur-sm">
				<div className="flex items-center gap-3">
					<div className="flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-red-500 to-blue-500 shadow-sm">
						<Zap className="size-5 text-white" />
					</div>
					<div>
						<h1 className="text-lg font-bold tracking-tight text-slate-800">
							Tug-of-War: Net Force
						</h1>
						<p className="text-xs text-slate-400">
							Add pullers and observe unbalanced forces
						</p>
					</div>
				</div>

				{/* Force summary */}
				<div className="flex items-center gap-4">
					<ForcePill
						label="Left"
						value={leftForce}
						direction="left"
						color="red"
					/>
					<div className="text-center min-w-[90px]">
						<div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">
							Net Force
						</div>
						<div
							className={cn(
								"text-lg font-extrabold leading-tight",
								netForce === 0
									? "text-slate-400"
									: netForce > 0
										? "text-blue-600"
										: "text-red-600"
							)}
						>
							{netForce === 0
								? "0 N"
								: `${Math.abs(netForce)} N ${netForce > 0 ? "→" : "←"}`}
						</div>
					</div>
					<ForcePill
						label="Right"
						value={rightForce}
						direction="right"
						color="blue"
					/>
				</div>

				{/* Action buttons */}
				<div className="flex items-center gap-2">
					<Button
						onClick={toggleAnimation}
						disabled={pullers.length === 0}
						size="sm"
						className={cn(
							"gap-1.5 shadow-sm transition-all",
							netForce > 0
								? "bg-blue-600 hover:bg-blue-700"
								: netForce < 0
									? "bg-red-600 hover:bg-red-700"
									: "bg-slate-700 hover:bg-slate-800"
						)}
					>
						{isAnimating ? (
							<>
								<Pause className="size-3.5" /> Pause
							</>
						) : (
							<>
								<Play className="size-3.5" /> Simulate
							</>
						)}
					</Button>
					<Button
						onClick={reset}
						variant="outline"
						size="sm"
						className="gap-1.5 border-slate-200"
					>
						<RotateCcw className="size-3.5" />
						Reset
					</Button>
					{pullers.length > 0 && (
						<Button
							onClick={clearAll}
							variant="ghost"
							size="sm"
							className="gap-1.5 text-slate-400 hover:text-red-500"
						>
							<Trash2 className="size-3.5" />
							Clear
						</Button>
					)}
				</div>
			</div>

			{/* Main content */}
			<div className="flex-1 flex min-h-0">
				{/* Left panel */}
				<TeamPanel
					side="left"
					pullers={leftPullers}
					force={newForceLeft}
					onForceChange={setNewForceLeft}
					onAdd={() => addPuller("left")}
					onRemove={removePuller}
				/>

				{/* Visualization (center) */}
				<div className="flex-1 flex flex-col min-w-0">
					{/* Scene */}
					<div className="flex-1 relative overflow-hidden">
						{/* Sky gradient */}
						<div className="absolute inset-0 bg-gradient-to-b from-sky-200 via-sky-100 to-emerald-50" />

						{/* Clouds */}
						<Cloud x="10%" y="12%" scale={1} />
						<Cloud x="70%" y="8%" scale={0.8} />
						<Cloud x="40%" y="20%" scale={0.6} />

						{/* Sun */}
						<div
							className="absolute w-16 h-16 rounded-full"
							style={{
								top: "8%",
								right: "12%",
								background: "radial-gradient(circle, #fde68a 0%, #fbbf24 40%, transparent 70%)",
								boxShadow: "0 0 60px 20px rgba(251, 191, 36, 0.3)",
							}}
						/>

						{/* Mountains (background) */}
						<svg
							className="absolute bottom-[20%] left-0 right-0 w-full"
							viewBox="0 0 1200 200"
							preserveAspectRatio="none"
							style={{ height: "30%" }}
						>
							<polygon
								points="0,200 100,80 200,140 350,40 500,120 650,60 800,130 950,30 1100,100 1200,70 1200,200"
								fill="#86efac"
								opacity="0.3"
							/>
							<polygon
								points="0,200 150,100 300,150 450,70 600,130 750,80 900,140 1050,60 1200,120 1200,200"
								fill="#4ade80"
								opacity="0.25"
							/>
						</svg>

						{/* Ground */}
						<div className="absolute bottom-0 left-0 right-0 h-[20%] bg-gradient-to-t from-amber-300/80 via-amber-200/60 to-green-300/40" />
						{/* Grass line */}
						<div
							className="absolute left-0 right-0 h-[3px]"
							style={{
								bottom: "20%",
								background: "linear-gradient(90deg, #16a34a, #22c55e, #16a34a, #22c55e, #16a34a)",
								opacity: 0.6,
							}}
						/>

						{/* Dirt texture lines */}
						<div className="absolute bottom-[5%] left-[10%] right-[10%] h-[1px] bg-amber-400/30" />
						<div className="absolute bottom-[10%] left-[20%] right-[15%] h-[1px] bg-amber-400/20" />
						<div className="absolute bottom-[15%] left-[5%] right-[25%] h-[1px] bg-amber-400/15" />

						{/* Center marker */}
						<div className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center" style={{ bottom: "20%" }}>
							<div className="text-[9px] font-medium text-slate-400/60 tracking-wider uppercase mb-0.5">
								center
							</div>
							<div className="w-[2px] h-8 bg-slate-400/30 rounded-full" />
						</div>

						{/* Animated group: pullers + rope + cart */}
						<div
							className="absolute inset-0 transition-none"
							style={{
								transform: `translateX(${cartOffset}px)`,
							}}
						>
							{/* Rope */}
							<div
								className="absolute rounded-full"
								style={{
									left: `calc(50% - ${CART_WIDTH / 2 + leftPullers.length * 48 + ROPE_EXTRA}px)`,
									right: `calc(50% - ${CART_WIDTH / 2 + rightPullers.length * 48 + ROPE_EXTRA}px)`,
									bottom: "calc(20% + 38px)",
									height: "4px",
									background: "linear-gradient(90deg, #92400e, #b45309, #92400e)",
									boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
								}}
							/>

							{/* Left pullers */}
							{leftPullers.map((p, i) => (
								<div
									key={p.id}
									className="absolute flex flex-col items-center"
									style={{
										right: `calc(50% + ${CART_WIDTH / 2 + 14 + i * 48}px)`,
										bottom: "calc(20% + 4px)",
									}}
								>
									<StickFigure
										color={p.color}
										facingLeft
									/>
									<span
										className="text-[10px] font-bold mt-0.5 drop-shadow-sm"
										style={{ color: p.color }}
									>
										{p.force}N
									</span>
								</div>
							))}

							{/* Right pullers */}
							{rightPullers.map((p, i) => (
								<div
									key={p.id}
									className="absolute flex flex-col items-center"
									style={{
										left: `calc(50% + ${CART_WIDTH / 2 + 14 + i * 48}px)`,
										bottom: "calc(20% + 4px)",
									}}
								>
									<StickFigure
										color={p.color}
										facingLeft={false}
									/>
									<span
										className="text-[10px] font-bold mt-0.5 drop-shadow-sm"
										style={{ color: p.color }}
									>
										{p.force}N
									</span>
								</div>
							))}

							{/* Cart */}
							<div
								className="absolute"
								style={{
									left: `calc(50% - ${CART_WIDTH / 2}px)`,
									bottom: "calc(20% + 6px)",
								}}
							>
								<svg
									width={CART_WIDTH}
									height="48"
									viewBox="0 0 72 48"
								>
									{/* Shadow */}
									<ellipse
										cx="36"
										cy="46"
										rx="30"
										ry="3"
										fill="rgba(0,0,0,0.1)"
									/>
									{/* Cart body */}
									<rect
										x="4"
										y="4"
										width="64"
										height="28"
										rx="5"
										fill="url(#cartGrad)"
										stroke="#374151"
										strokeWidth="2"
									/>
									{/* Cart shine */}
									<rect
										x="8"
										y="7"
										width="56"
										height="8"
										rx="3"
										fill="rgba(255,255,255,0.15)"
									/>
									{/* Cart detail lines */}
									<line
										x1="12"
										y1="20"
										x2="60"
										y2="20"
										stroke="rgba(255,255,255,0.1)"
										strokeWidth="1"
									/>
									{/* Axle */}
									<rect
										x="12"
										y="30"
										width="48"
										height="3"
										rx="1"
										fill="#1f2937"
									/>
									{/* Left wheel */}
									<circle
										cx="20"
										cy="38"
										r="7"
										fill="#1f2937"
										stroke="#111827"
										strokeWidth="1.5"
									/>
									<circle
										cx="20"
										cy="38"
										r="4"
										fill="#374151"
									/>
									<circle
										cx="20"
										cy="38"
										r="2"
										fill="#6b7280"
									/>
									{/* Right wheel */}
									<circle
										cx="52"
										cy="38"
										r="7"
										fill="#1f2937"
										stroke="#111827"
										strokeWidth="1.5"
									/>
									<circle
										cx="52"
										cy="38"
										r="4"
										fill="#374151"
									/>
									<circle
										cx="52"
										cy="38"
										r="2"
										fill="#6b7280"
									/>
									<defs>
										<linearGradient
											id="cartGrad"
											x1="0"
											y1="0"
											x2="0"
											y2="1"
										>
											<stop
												offset="0%"
												stopColor="#6b7280"
											/>
											<stop
												offset="100%"
												stopColor="#4b5563"
											/>
										</linearGradient>
									</defs>
								</svg>
							</div>
						</div>

						{/* Net force indicator overlay */}
						{netForce !== 0 && isAnimating && (
							<div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
								<div
									className={cn(
										"flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold text-white shadow-lg backdrop-blur-sm",
										netForce > 0
											? "bg-blue-500/90"
											: "bg-red-500/90"
									)}
								>
									{netForce < 0 && "←"}
									{Math.abs(netForce)}N net
									{netForce > 0 && " →"}
								</div>
							</div>
						)}

						{/* Empty state overlay */}
						{pullers.length === 0 && (
							<div className="absolute inset-0 flex items-center justify-center">
								<div className="bg-white/80 backdrop-blur-sm rounded-xl px-6 py-4 shadow-sm border border-slate-200/50 text-center">
									<p className="text-sm font-medium text-slate-600">
										Add pullers to each side to begin
									</p>
									<p className="text-xs text-slate-400 mt-1">
										Use the panels on the left and right
									</p>
								</div>
							</div>
						)}
					</div>

					{/* Bottom bar: motion label + force diagram */}
					<div className="flex-shrink-0 border-t border-slate-200/60 bg-white/70 backdrop-blur-sm">
						{/* Motion prediction */}
						<div className="flex items-center justify-center gap-3 px-6 py-2">
							<p className={cn("text-sm font-semibold", motion.color)}>
								{motion.text}
							</p>
						</div>

						{/* Force diagram */}
						{pullers.length > 0 && (
							<div className="px-6 pb-3">
								<div className="flex items-center gap-4">
									{/* Force arrows visualization */}
									<div className="flex-1 relative h-10 flex items-center">
										<div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-200 -translate-x-1/2" />

										{leftForce > 0 && (
											<div
												className="absolute right-1/2 h-7 top-1/2 -translate-y-1/2 flex items-center"
												style={{
													width: `${Math.min(leftForce * 1.2, 200)}px`,
												}}
											>
												<div className="relative w-full h-5 bg-gradient-to-r from-red-500 to-red-400 rounded-r-sm flex items-center justify-center">
													<div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2">
														<div className="w-0 h-0 border-t-[12px] border-t-transparent border-b-[12px] border-b-transparent border-r-[10px] border-r-red-500" />
													</div>
													<span className="text-[10px] font-bold text-white relative z-10">
														{leftForce}N
													</span>
												</div>
											</div>
										)}

										{rightForce > 0 && (
											<div
												className="absolute left-1/2 h-7 top-1/2 -translate-y-1/2 flex items-center justify-end"
												style={{
													width: `${Math.min(rightForce * 1.2, 200)}px`,
												}}
											>
												<div className="relative w-full h-5 bg-gradient-to-l from-blue-500 to-blue-400 rounded-l-sm flex items-center justify-center">
													<div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2">
														<div className="w-0 h-0 border-t-[12px] border-t-transparent border-b-[12px] border-b-transparent border-l-[10px] border-l-blue-500" />
													</div>
													<span className="text-[10px] font-bold text-white relative z-10">
														{rightForce}N
													</span>
												</div>
											</div>
										)}
									</div>

									{/* Equation */}
									<div className="flex-shrink-0 text-xs text-slate-500">
										<span className="text-red-500 font-semibold">{leftForce}N</span>
										{" — "}
										<span className="text-blue-500 font-semibold">{rightForce}N</span>
										{" = "}
										<span
											className={cn(
												"font-bold",
												netForce === 0
													? "text-slate-600"
													: netForce > 0
														? "text-blue-600"
														: "text-red-600"
											)}
										>
											{netForce === 0
												? "0N (Balanced)"
												: `${Math.abs(netForce)}N ${netForce > 0 ? "right" : "left"}`}
										</span>
									</div>
								</div>

								<p className="text-[10px] text-slate-400 text-center mt-1">
									{netForce === 0
										? "Forces are balanced — the cart stays still (Newton's 1st Law)"
										: "Forces are unbalanced — the cart accelerates toward the net force (Newton's 2nd Law)"}
								</p>
							</div>
						)}
					</div>
				</div>

				{/* Right panel */}
				<TeamPanel
					side="right"
					pullers={rightPullers}
					force={newForceRight}
					onForceChange={setNewForceRight}
					onAdd={() => addPuller("right")}
					onRemove={removePuller}
				/>
			</div>
		</div>
	);
}

function ForcePill({
	label,
	value,
	direction,
	color,
}: {
	label: string;
	value: number;
	direction: "left" | "right";
	color: "red" | "blue";
}) {
	return (
		<div
			className={cn(
				"flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold border",
				color === "red"
					? "border-red-200 text-red-600 bg-red-50"
					: "border-blue-200 text-blue-600 bg-blue-50"
			)}
		>
			{direction === "left" && "←"}
			{value}N
			{direction === "right" && " →"}
		</div>
	);
}

function TeamPanel({
	side,
	pullers,
	force,
	onForceChange,
	onAdd,
	onRemove,
}: {
	side: "left" | "right";
	pullers: Puller[];
	force: number;
	onForceChange: (v: number) => void;
	onAdd: () => void;
	onRemove: (id: number) => void;
}) {
	const isLeft = side === "left";
	const colorName = isLeft ? "red" : "blue";

	return (
		<div
			className={cn(
				"w-64 flex-shrink-0 flex flex-col border-slate-200/60 bg-white/50 backdrop-blur-sm",
				isLeft ? "border-r" : "border-l"
			)}
		>
			{/* Panel header */}
			<div
				className={cn(
					"flex-shrink-0 px-4 py-3 border-b",
					isLeft
						? "border-red-100 bg-gradient-to-r from-red-50/80 to-transparent"
						: "border-blue-100 bg-gradient-to-l from-blue-50/80 to-transparent"
				)}
			>
				<div className="flex items-center gap-2">
					<div
						className={cn(
							"w-2.5 h-2.5 rounded-full",
							isLeft ? "bg-red-500" : "bg-blue-500"
						)}
					/>
					<h2
						className={cn(
							"text-sm font-bold",
							isLeft ? "text-red-700" : "text-blue-700"
						)}
					>
						{isLeft ? "← Left Team" : "Right Team →"}
					</h2>
					{pullers.length > 0 && (
						<Badge
							variant="outline"
							className={cn(
								"ml-auto text-[10px] px-1.5 py-0",
								isLeft
									? "border-red-200 text-red-500"
									: "border-blue-200 text-blue-500"
							)}
						>
							{pullers.length}/5
						</Badge>
					)}
				</div>
			</div>

			{/* Force slider */}
			<div className="flex-shrink-0 px-4 py-3 space-y-2 border-b border-slate-100">
				<div className="flex items-center justify-between">
					<span className="text-xs font-medium text-slate-500">
						Force
					</span>
					<span
						className={cn(
							"text-sm font-bold tabular-nums",
							isLeft ? "text-red-600" : "text-blue-600"
						)}
					>
						{force}N
					</span>
				</div>
				<Slider
					value={[force]}
					onValueChange={([v]) => onForceChange(v ?? 50)}
					min={10}
					max={100}
					step={5}
					className={cn(
						isLeft
							? "[&_[data-slot=slider-thumb]]:bg-red-500 [&_[data-slot=slider-range]]:bg-red-400 [&_[data-slot=slider-thumb]]:border-red-600"
							: "[&_[data-slot=slider-thumb]]:bg-blue-500 [&_[data-slot=slider-range]]:bg-blue-400 [&_[data-slot=slider-thumb]]:border-blue-600"
					)}
				/>
				<Button
					onClick={onAdd}
					variant="outline"
					size="sm"
					className={cn(
						"w-full text-xs gap-1",
						isLeft
							? "border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
							: "border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300"
					)}
					disabled={pullers.length >= 5}
				>
					<Plus className="size-3.5" />
					Add {isLeft ? "Left" : "Right"} Puller
				</Button>
			</div>

			{/* Puller list */}
			<div className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
				{pullers.length === 0 && (
					<div className="flex items-center justify-center h-full">
						<p className="text-xs text-slate-300 text-center">
							No pullers yet
						</p>
					</div>
				)}
				{pullers.map((p) => (
					<div
						key={p.id}
						className={cn(
							"flex items-center justify-between rounded-lg border px-3 py-2 transition-all hover:shadow-sm",
							isLeft
								? "border-red-100 bg-red-50/50 hover:bg-red-50"
								: "border-blue-100 bg-blue-50/50 hover:bg-blue-50"
						)}
					>
						<div className="flex items-center gap-2">
							<div
								className="size-3 rounded-full ring-2 ring-white shadow-sm"
								style={{
									backgroundColor: p.color,
								}}
							/>
							<span className="text-sm font-semibold text-slate-700">
								{p.force}N
							</span>
						</div>
						<button
							onClick={() => onRemove(p.id)}
							className={cn(
								"rounded-md p-1 transition-colors",
								isLeft
									? "text-slate-300 hover:text-red-500 hover:bg-red-100"
									: "text-slate-300 hover:text-blue-500 hover:bg-blue-100"
							)}
						>
							<X className="size-3.5" />
						</button>
					</div>
				))}
			</div>

			{/* Total */}
			{pullers.length > 0 && (
				<div
					className={cn(
						"flex-shrink-0 px-4 py-2 border-t text-sm flex items-center justify-between",
						isLeft
							? "border-red-100 bg-red-50/30"
							: "border-blue-100 bg-blue-50/30"
					)}
				>
					<span className="text-slate-400 text-xs font-medium">
						Total
					</span>
					<span
						className={cn(
							"font-bold",
							isLeft ? "text-red-600" : "text-blue-600"
						)}
					>
						{pullers.reduce((sum, p) => sum + p.force, 0)}N
					</span>
				</div>
			)}
		</div>
	);
}

function Cloud({ x, y, scale }: { x: string; y: string; scale: number }) {
	return (
		<div
			className="absolute pointer-events-none"
			style={{
				left: x,
				top: y,
				transform: `scale(${scale})`,
				opacity: 0.6,
			}}
		>
			<svg width="80" height="36" viewBox="0 0 80 36">
				<ellipse cx="30" cy="22" rx="20" ry="12" fill="white" />
				<ellipse cx="50" cy="20" rx="18" ry="14" fill="white" />
				<ellipse cx="40" cy="14" rx="16" ry="12" fill="white" />
				<ellipse cx="22" cy="18" rx="12" ry="10" fill="white" />
				<ellipse cx="58" cy="22" rx="12" ry="10" fill="white" />
			</svg>
		</div>
	);
}

function StickFigure({
	color,
	facingLeft,
}: {
	color: string;
	facingLeft: boolean;
}) {
	return (
		<svg
			width="32"
			height="50"
			viewBox="0 0 32 50"
			style={{ transform: facingLeft ? "scaleX(-1)" : undefined }}
		>
			{/* Shadow */}
			<ellipse cx="16" cy="48" rx="8" ry="2" fill="rgba(0,0,0,0.1)" />
			{/* Head */}
			<circle cx="16" cy="7" r="6" fill={color} />
			{/* Eye */}
			<circle cx="19" cy="6" r="1" fill="white" opacity="0.6" />
			{/* Body */}
			<line
				x1="16"
				y1="13"
				x2="16"
				y2="30"
				stroke={color}
				strokeWidth="3"
				strokeLinecap="round"
			/>
			{/* Arms - pulling pose */}
			<line
				x1="16"
				y1="18"
				x2="5"
				y2="24"
				stroke={color}
				strokeWidth="2.5"
				strokeLinecap="round"
			/>
			<line
				x1="16"
				y1="22"
				x2="5"
				y2="28"
				stroke={color}
				strokeWidth="2.5"
				strokeLinecap="round"
			/>
			{/* Legs - bracing */}
			<line
				x1="16"
				y1="30"
				x2="7"
				y2="44"
				stroke={color}
				strokeWidth="2.5"
				strokeLinecap="round"
			/>
			<line
				x1="16"
				y1="30"
				x2="23"
				y2="44"
				stroke={color}
				strokeWidth="2.5"
				strokeLinecap="round"
			/>
		</svg>
	);
}
